import { useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ClientDataTable.module.css';
import {
  DataTable,
  type Column,
  type DataTableProps,
  type ListStatus,
  type Row,
} from './DataTable';
import { Input } from '../../primitives/Input/Input';
import { Combobox } from '../../primitives/Combobox/Combobox';
import { Select } from '../../primitives/Select/Select';
import { Pagination } from '../Pagination/Pagination';
import { useClientPages } from '../Pagination/useClientPages';

/**
 * ClientDataTable — search, sort and paging for a list whose rows are ALL
 * already in the browser.
 *
 * ## What this is NOT for
 *
 * Twelve of this application's lists are cursor-paginated on the server
 * (`CaseList`, `RequestList`, `CodeList`, `AuditLogScreen`, ...). Wrapping one
 * of those in this component would search and sort THE TWENTY-FIVE ROWS ON
 * SCREEN while presenting itself as a search of the list — an agent typing a
 * case number into it would be told, truthfully about the page and falsely
 * about the database, that no such case exists. Those screens need `sort` and
 * `q` on the endpoint, which the API does not have yet. Use this component
 * only where the fetch returned everything: the reference hierarchy, the user
 * catalogue, the template catalogue, the sessions list, the import previews,
 * the report tables.
 *
 * ## The three tools, and what each one is measured against
 *
 * **Search** matches against every primitive value on the row object, not
 * against the rendered cells. A cell is frequently a `StatusPill`, a
 * `<bdi>`-wrapped number or a bilingual name assembled by a `render`
 * callback, and none of those are text this component can read. The row's own
 * fields ARE that text's source, so `city.name_ar` is searchable on a screen
 * whose visible column shows `name_en`, which is the behaviour an Arabic
 * speaker expects and the reverse of what matching on rendered output would
 * give them.
 *
 * **Sort** is offered on a column only when at least one row carries a raw,
 * non-null value at that column's `key`. A `render`-only column — "Actions",
 * a computed badge with no backing field — has nothing to compare, and a sort
 * control that reorders nothing is worse than no control. A caller can still
 * suppress one explicitly with `sortable: false`.
 *
 * Comparison goes through `Intl.Collator` in the ACTIVE language, with
 * `numeric: true` so `C-2` sorts before `C-10`, and `sensitivity: 'base'` so
 * case and Arabic diacritics do not split a run. A column marked `numeric` is
 * compared as a number whenever both sides parse as one.
 *
 * **Paging** reuses `Pagination` through `useClientPages`, so the control is
 * the same one every server-paginated screen already shows — same landmark,
 * same accessible names, same page sizes. It appears only past 25 rows, the
 * smallest page size: a pager under a sixteen-row list is furniture.
 *
 * ## Filters are DECLARED, not implemented by the caller
 *
 * A search box answers "find me this row". It does not answer "show me only
 * the inactive ones" — the reader has to know that "inactive" is a word that
 * appears somewhere on the row, and on a screen whose Status column is
 * rendered from an `is_active` boolean it is not a word at all. That is why
 * every one of these lists looked unfiltered however much searching it
 * offered.
 *
 * `filters` is a list of facets this component OWNS: it holds their state,
 * applies them before the search, returns the reader to page one when one
 * changes, and clears them alongside the query from the no-results recovery.
 * A caller declares what the choices are and how a row matches one; it never
 * filters its own `rows`, because a caller that did would also have to
 * re-implement the empty-vs-no-results distinction below and would get it
 * wrong in a different way on each screen.
 *
 * The default match is `String(row[key]) === value`, which covers a status
 * enum, a boolean and a foreign key. `match` is for the rest.
 *
 * ## Empty vs no-results is decided HERE, not by the caller
 *
 * The caller reports `'empty'` when the fetch returned nothing at all. When a
 * search narrows a non-empty list to zero, that is `'no-results'` — a
 * different state with a different recovery ("clear the search", not "create
 * the first record"), and `DataTable` already renders both. Getting this
 * wrong is how a list tells an agent their data is gone.
 */

export interface TableFilter {
  /** Stable within one table; also the key this filter's value is held under. */
  id: string;
  /** The select's visible label. Short — it sits in a row with the others. */
  label: string;
  /**
   * The choices, excluding "all". The all-option is added by this component
   * with the shared `table.filterAll` label, so no caller has to invent its
   * own wording for the same idea.
   */
  options: readonly { value: string; label: string }[];
  /** Row field compared against the chosen value. Ignored when `match` is given. */
  key?: string;
  /** Full control, for a facet no single field answers. */
  match?: (row: Row, value: string) => boolean;
}

export interface ClientDataTableProps extends Omit<
  DataTableProps,
  'sort' | 'onSort' | 'totalCount' | 'onClearFilters'
> {
  /** EVERY row, unpaged and unfiltered. */
  rows?: readonly Row[];
  /** Set `false` to drop the search box (a table of five fixed rows). */
  searchable?: boolean;
  /** Rows per page before the reader changes it. */
  initialPerPage?: number;
  /** Rendered at the end of the toolbar row — "New city", a filter chip. */
  toolbarActions?: ReactNode;
  /** Facets this component owns. See the docblock. */
  filters?: readonly TableFilter[];
}

/** The smallest page size `Pagination` offers; below it there is nothing to page. */
const MIN_PAGE_SIZE = 10;

function searchableText(row: Row): string {
  const parts: string[] = [];
  for (const value of Object.values(row)) {
    if (typeof value === 'string') parts.push(value);
    else if (typeof value === 'number' || typeof value === 'boolean') parts.push(String(value));
  }
  return parts.join(' ').toLowerCase();
}

function rawOf(row: Row, key: string): unknown {
  const value = row[key];
  return value === null || value === undefined || value === '' ? undefined : value;
}

export function ClientDataTable({
  rows = [],
  columns,
  status,
  searchable = true,
  initialPerPage = MIN_PAGE_SIZE,
  toolbarActions,
  filters = [],
  noResults,
  ...rest
}: ClientDataTableProps) {
  const { t, i18n } = useTranslation();

  const [query, setQuery] = useState('');
  // The typed value drives the input; a deferred copy drives the filter, so a
  // 631-row re-filter never sits between a keystroke and its character.
  const deferredQuery = useDeferredValue(query);
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' } | undefined>(
    undefined,
  );
  /** Facet id -> chosen value. An absent id, and `''`, both mean "all". */
  const [facets, setFacets] = useState<Readonly<Record<string, string>>>({});

  const collator = useMemo(
    () => new Intl.Collator(i18n.language, { numeric: true, sensitivity: 'base' }),
    [i18n.language],
  );

  /** A column is sortable when SOME row has a raw value under its key. */
  const resolvedColumns = useMemo<readonly Column[]>(
    () =>
      columns.map((column) => {
        if (column.sortable === false) return column;
        const comparable = rows.some((row) => rawOf(row, column.key) !== undefined);
        return { ...column, sortable: comparable };
      }),
    [columns, rows],
  );

  const haystacks = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) map.set(row.id, searchableText(row));
    return map;
  }, [rows]);

  /**
   * Facets first, then the search — so the row count beside the search box
   * reads "how many of the filtered rows match what I typed", which is the
   * question the reader is asking with both controls set.
   */
  const faceted = useMemo(() => {
    const active = filters.filter((filter) => (facets[filter.id] ?? '') !== '');
    if (active.length === 0) return rows;
    return rows.filter((row) =>
      active.every((filter) => {
        const value = facets[filter.id] as string;
        if (filter.match !== undefined) return filter.match(row, value);
        if (filter.key === undefined) return true;
        const raw = row[filter.key];
        // `String(...)` on both sides: a facet value arrives from a <select>
        // as text, and the row may hold a boolean or a number.
        return raw !== null && raw !== undefined && String(raw) === value;
      }),
    );
  }, [rows, filters, facets]);

  const filtered = useMemo(() => {
    const needle = deferredQuery.trim().toLowerCase();
    if (needle === '') return faceted;
    return faceted.filter((row) => (haystacks.get(row.id) ?? '').includes(needle));
  }, [faceted, haystacks, deferredQuery]);

  const sorted = useMemo(() => {
    if (sort === undefined) return filtered;
    const column = resolvedColumns.find((c) => c.key === sort.field);
    if (column === undefined) return filtered;
    const sign = sort.direction === 'asc' ? 1 : -1;

    // A copy: `filtered` is either `rows` itself (the caller's array) or a
    // fresh one from `filter`, and sorting the former in place would reorder
    // state the caller still owns.
    return [...filtered].sort((a, b) => {
      const left = rawOf(a, sort.field);
      const right = rawOf(b, sort.field);
      // Missing values sink to the bottom in BOTH directions. A column sorted
      // descending that leads with a screenful of em dashes has buried the
      // thing the reader asked to see.
      if (left === undefined && right === undefined) return 0;
      if (left === undefined) return 1;
      if (right === undefined) return -1;

      if (typeof left === 'number' && typeof right === 'number') return (left - right) * sign;
      if (column.numeric === true) {
        const nl = Number(left);
        const nr = Number(right);
        if (!Number.isNaN(nl) && !Number.isNaN(nr)) return (nl - nr) * sign;
      }
      return collator.compare(String(left), String(right)) * sign;
    });
  }, [filtered, sort, resolvedColumns, collator]);

  const total = sorted.length;
  const pages = useClientPages(total, initialPerPage);

  // A new search is a new list; staying on page 4 of it shows an empty table
  // over a non-empty result. `first()` is a no-op when already there, so this
  // cannot loop.
  const firstPage = pages.first;
  // The facets join the query in one key: both narrow the list, and both leave
  // the reader on a page that may no longer exist.
  const narrowing = `${deferredQuery} ${JSON.stringify(facets)}`;
  const previousNarrowing = useRef(narrowing);
  useEffect(() => {
    if (previousNarrowing.current === narrowing) return;
    previousNarrowing.current = narrowing;
    firstPage();
  }, [narrowing, firstPage]);

  const from = pages.pageIndex * pages.perPage;
  const page = useMemo(
    () => sorted.slice(from, from + pages.perPage),
    [sorted, from, pages.perPage],
  );

  const anyFacet = filters.some((filter) => (facets[filter.id] ?? '') !== '');
  const searching = deferredQuery.trim() !== '' || anyFacet;
  // A filter that empties the list reports NO-RESULTS, not EMPTY. "No records
  // yet — create the first one" over a list of 631 rows the reader has just
  // narrowed to zero is the single most alarming thing this table can say.
  const resolvedStatus: ListStatus =
    status === 'ready' && searching && total === 0 ? 'no-results' : status;

  return (
    <div className={styles['wrap']}>
      {/*
        ONE card: the toolbar is the card's head row and the table sits under
        it in the same card (the table's own card chrome is switched off by
        its `--table-card-*` hooks, set on `.card`). Two stacked cards read as
        two unrelated things; the search belongs to the rows it searches.
      */}
      <div className={styles['card']} data-testid="clientTableCard">
      {searchable || toolbarActions !== undefined || filters.length > 0 ? (
        <div className={styles['toolbar']}>
          {searchable ? (
            <Input
              className={styles['search']}
              label={t('table.search')}
              width="fill"
              type="search"
              value={query}
              disabled={status === 'loading' || status === 'error'}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
            />
          ) : null}
          {filters.length === 0 ? null : (
            <div className={styles['filters']}>
              {filters.map((filter) => {
                // The all-option carries the EMPTY string, so "no choice
                // made" and "the reader chose all" are the same state.
                // Two states that render identically are one state with a
                // spare, and the spare is what a Clear button forgets.
                const options = [{ value: '', label: t('table.filterAll') }, ...filter.options];
                const disabled = status === 'loading' || status === 'error';
                const choose = (chosen: string) => {
                  setFacets((current) => ({ ...current, [filter.id]: chosen }));
                };

                // Handoff, Forms 5: "Any select over ~8 options is a combobox
                // with type-ahead." A city filter over the national city list
                // is exactly that case, so a long facet becomes the
                // searchable dropdown rather than a native select to scroll.
                return filter.options.length > 8 ? (
                  <Combobox
                    key={filter.id}
                    label={filter.label}
                    width="compact"
                    options={options}
                    value={facets[filter.id] ?? ''}
                    emptyMessage={t('table.filterNoMatches')}
                    disabled={disabled}
                    onChange={choose}
                  />
                ) : (
                  <Select
                    key={filter.id}
                    label={filter.label}
                    width="compact"
                    value={facets[filter.id] ?? ''}
                    disabled={disabled}
                    options={options}
                    onChange={(event) => {
                      choose(event.target.value);
                    }}
                  />
                );
              })}
            </div>
          )}

          <div className={styles['actions']}>
            {searching && status === 'ready' ? (
              // aria-hidden: the table's own polite region announces the same
              // count, and two regions saying "3 rows" say it twice.
              <p className={styles['count']} aria-hidden="true">
                {t('table.rowCount', { count: total })}
              </p>
            ) : null}
            {toolbarActions}
          </div>
        </div>
      ) : null}

      <DataTable
        {...rest}
        columns={resolvedColumns}
        rows={page}
        status={resolvedStatus}
        totalCount={total}
        {...(sort === undefined ? {} : { sort })}
        onSort={(field) => {
          setSort((current) =>
            current?.field === field
              ? { field, direction: current.direction === 'asc' ? 'desc' : 'asc' }
              : { field, direction: 'asc' },
          );
          firstPage();
        }}
        onClearFilters={() => {
          setQuery('');
          // KILL: clear only the query. The reader presses the one control
          // the empty table offers them and the table stays empty, because
          // the facet that emptied it is still set.
          setFacets({});
        }}
        // `query` is what makes the no-results state name the search back to
        // the reader — "Nothing matches 'jedah'" is a typo they can see, where
        // a bare "No matches" is a list they conclude is broken.
        noResults={{ ...noResults, query: deferredQuery.trim() }}
      />
      </div>

      {resolvedStatus === 'ready' && total > MIN_PAGE_SIZE ? (
        <Pagination pages={pages} total={total} rowsOnPage={page.length} />
      ) : null}
    </div>
  );
}
